import {
  CircleCheckIcon,
  CircleIntersectIcon,
  OverviewIcon,
  SparkleIcon,
  UnorderedListIcon,
} from '@otter-ai/den-icons';
import type {
  Workspace,
  WorkspaceDomain,
  WorkspaceExtra,
} from '@otter-ai/recall-client';
import { type ReactNode, useMemo } from 'react';
import { PATH_URL } from '@/constants/path-constants';

export const createUrl = (path: string) => new URL(path, location.href);

export function redirectUrl(type: 'personal' | 'admin'): string {
  return type === 'admin'
    ? PATH_URL.TEAM_TEMPLATE_BUILDER
    : PATH_URL.SETTINGS_PERSONAL_TEMPLATES_BUILDER;
}

export const TITLES_MAP: { [key: string]: string } = {
  insights: 'Insights',
  outline: 'Outline',
  custom_prompt: 'Custom Prompt',
  abstract_summary: 'Overview',
  action_items: 'Action Items',
};

export const ICONS_MAP: { [key: string]: ReactNode } = {
  insights: <SparkleIcon />,
  outline: <UnorderedListIcon />,
  custom_prompt: <CircleIntersectIcon />,
  abstract_summary: <OverviewIcon />,
  action_items: <CircleCheckIcon />,
};

export const TOOLTIPS_MAP: { [key: string]: string } = {
  insights: 'A detailed breakdown of key topics and points discussed.',
  outline:
    'Custom topics or questions to extract precise information from the meeting.',
  custom_prompt:
    'Full flexibility in defining the content and format you want from the meeting.',
  abstract_summary: 'A concise summary of the meeting’s essential details.',
  action_items:
    'AI-detected tasks and responsibilities assigned to participants.',
};

// TODO(sleahy): Use CSS gradient instead of SVGs
const gradients = [
  'bg-[url(/nextjs/svgs/TemplateAssets/bg-template-1.webp)]',
  'bg-[url(/nextjs/svgs/TemplateAssets/bg-template-2.webp)]',
  'bg-[url(/nextjs/svgs/TemplateAssets/bg-template-3.webp)]',
  'bg-[url(/nextjs/svgs/TemplateAssets/bg-template-4.webp)]',
  'bg-[url(/nextjs/svgs/TemplateAssets/bg-template-5.webp)]',
  'bg-[url(/nextjs/svgs/TemplateAssets/bg-template-6.webp)]',
  'bg-[url(/nextjs/svgs/TemplateAssets/bg-template-7.webp)]',
];

export const getTooltip = (blockType: string) => TOOLTIPS_MAP[blockType];
export const getIcon = (blockType: string) => ICONS_MAP[blockType];
export const getTitle = (blockType: string) => TITLES_MAP[blockType];
export const getGradientClass = (index?: number) => {
  return gradients[(index ?? 0) % gradients.length];
};

export function useWorkspaceMemberList(
  workspace?: Pick<
    Workspace & WorkspaceExtra,
    'settings' | 'domains' | 'members'
  > | null,
) {
  return useMemo(() => {
    if (!workspace) {
      return [];
    }

    function hasEnabledShareToDomainOnly(): boolean {
      return workspace?.settings?.share_to_domain_only ?? false;
    }

    function isEmailInDomain(userEmail: string): boolean {
      const domains =
        workspace?.domains?.map((domain: WorkspaceDomain) => domain.domain) ??
        [];
      return domains.some((domain: string) => userEmail.endsWith(domain));
    }

    return (
      workspace?.members?.filter((member: { member_email: string }) => {
        if (hasEnabledShareToDomainOnly()) {
          return isEmailInDomain(member.member_email);
        }
        return true;
      }) ?? []
    );
  }, [workspace]);
}
